// Keeps the store up to date like the app's SyncEngine (app/.../sync/SyncEngine.kt): pages of
// GET /v1/sync from a cursor, every object in a page is its full current state, removed_* means
// delete; a {"t":"sync"} frame or reconnect only triggers the next run. 410 RESYNC_REQUIRED: the
// events were pruned, so the conversation list is loaded again and the cursor starts over.

import { ApiError } from "../api/client.ts";
import type { ConversationListResponse, MessageListResponse, MessageResponse, SyncResponse, TopicListResponse } from "../api/contract.gen.ts";
import type { Session } from "../auth/session.ts";
import type { ServerFrame } from "../realtime/realtime.ts";
import { fromBase64 } from "./bytes.ts";
import { decryptContent } from "./content.ts";
import type { Store } from "./store.ts";

/** How long an activity ("nimmt auf …", "schreibt …") shows without being repeated. */
const ACTIVITY_MS = 9_000;

export class SyncEngine {
  /** active_calls of the last page of a run: the calls running right now. */
  onActiveCalls: (calls: SyncResponse["active_calls"]) => void = () => undefined;
  private cursor = 0;
  private running: Promise<void> | null = null;
  private again = false;
  private readonly session: Session;
  private readonly store: Store;

  constructor(session: Session, store: Store) {
    this.session = session;
    this.store = store;
  }

  /** First load: the conversation list, then the events from the start. */
  async start(): Promise<void> {
    await this.loadConversations();
    await this.run();
  }

  /** Runs a sync now, or once more after the one running. */
  run(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.again = false;
        await this.pages();
      } while (this.again);
    })().finally(() => (this.running = null));
    return this.running;
  }

  /** A frame from the realtime connection. */
  onFrame(frame: ServerFrame): void {
    const s = this.store;
    switch (frame.t) {
      case "ready":
      case "sync":
        void this.run().catch(() => undefined);
        return;
      case "presence":
        if (Array.isArray(frame.users)) {
          s.online.clear();
          for (const u of frame.users as string[]) s.online.add(u);
          for (const [u, ms] of Object.entries((frame.last_seen as Record<string, number>) ?? {})) s.lastSeen.set(u, ms);
        } else if (typeof frame.user === "string") {
          if (frame.online) s.online.add(frame.user);
          else s.online.delete(frame.user);
          if (typeof frame.last_seen === "number") s.lastSeen.set(frame.user, frame.last_seen);
        }
        s.changed();
        return;
      case "live": {
        // Frames to the recorder (listening, reading) are about our own recording; the composer handles them.
        if (typeof frame.to === "string" || frame.listening !== undefined || frame.reading !== undefined) return;
        const conversation = frame.conversation as string;
        const user = frame.user as string;
        if (!conversation || !user || user === s.me) return;
        let byUser = s.live.get(conversation);
        if (!byUser) s.live.set(conversation, (byUser = new Map()));
        const current = byUser.get(user) ?? { user, state: "recording", text: false, audio: false, turns: new Map(), at: 0 };
        if (typeof frame.state === "string") {
          if (frame.state === "ended" || frame.state === "discarded") {
            byUser.delete(user);
            s.changed();
            return;
          }
          current.state = frame.state;
          if (typeof frame.text === "boolean") current.text = frame.text;
          if (typeof frame.audio === "boolean") current.audio = frame.audio;
        }
        const turn = frame.turn as { o: number; text: string; final: boolean } | undefined;
        if (turn) {
          current.turns.set(turn.o, { text: turn.text, final: turn.final });
          current.text = true;
        }
        current.at = Date.now();
        byUser.set(user, current);
        s.changed();
        return;
      }
      case "activity": {
        const conversation = frame.conversation as string;
        const user = frame.user as string;
        const list = (s.activity.get(conversation) ?? []).filter((a) => a.user !== user);
        if (frame.what !== "idle") list.push({ user, what: frame.what as string, until: Date.now() + ACTIVITY_MS });
        s.activity.set(conversation, list);
        s.changed();
        return;
      }
    }
  }

  /** The newest messages of a conversation (when it is opened), and fresh media links. */
  async loadMessages(conversationId: string, before?: string): Promise<number> {
    const query = new URLSearchParams({ limit: "200" });
    if (before) query.set("before", before);
    const page = await this.session.call<MessageListResponse>(`/v1/conversations/${conversationId}/messages?${query}`);
    await this.putMessages(page.messages);
    this.store.loaded.add(conversationId);
    this.store.changed();
    return page.messages.length;
  }

  async loadTopics(conversationId: string): Promise<void> {
    const list = await this.session.call<TopicListResponse>(`/v1/conversations/${conversationId}/topics`);
    for (const [id, topic] of this.store.topics) if (topic.conversation_id === conversationId) this.store.topics.delete(id);
    for (const topic of list.topics) this.store.topics.set(topic.id, topic);
    this.store.changed();
  }

  async loadConversations(): Promise<void> {
    const list = await this.session.call<ConversationListResponse>("/v1/conversations");
    const keep = new Set(list.conversations.map((c) => c.id));
    for (const id of [...this.store.conversations.keys()]) if (!keep.has(id)) this.store.removeConversation(id);
    for (const c of list.conversations) this.store.conversations.set(c.id, c);
    this.store.changed();
  }

  private async pages(): Promise<void> {
    for (;;) {
      let page: SyncResponse;
      try {
        page = await this.session.call<SyncResponse>(`/v1/sync?after=${this.cursor}&limit=500`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 410 && this.cursor > 0) {
          await this.loadConversations();
          this.cursor = 0;
          continue;
        }
        throw e;
      }
      for (const c of page.conversations) this.store.conversations.set(c.id, c);
      for (const id of page.removed_conversations) this.store.removeConversation(id);
      await this.putMessages(page.messages);
      for (const t of page.topics) this.store.topics.set(t.id, t);
      for (const id of page.removed_topics ?? []) this.store.topics.delete(id);
      for (const d of page.documents ?? []) this.store.documents.set(d.id, d);
      for (const id of page.removed_documents ?? []) this.store.documents.delete(id);
      this.cursor = page.next_seq;
      this.store.changed();
      if (!page.has_more) {
        this.onActiveCalls(page.active_calls ?? []);
        return;
      }
    }
  }

  private async putMessages(messages: MessageResponse[]): Promise<void> {
    await Promise.all(
      messages.map(async (response) => {
        const known = this.store.message(response.conversation_id, response.id);
        const mck = response.mck ? fromBase64(response.mck) : known?.mck;
        const content = await decryptContent(response, mck);
        this.store.putMessage({ response, mck, content });
      }),
    );
  }
}
